import {
  EstateDesignType,
  EstateLevel,
  EstateOwner,
  EstateType,
} from "@1games-ru/region-shared/dist/enums/estate/estate";
import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { isArray, isNull } from "lodash";
import { RootState } from "@/core/store";
import { EstateSelectTab } from "@/domains/estate/constants/estateSelectTab";
import { ImprovementsTab } from "@/domains/estate/constants/improvementsTab";
import { Tab } from "@/domains/estate/constants/tab";
import { VehiclesTab } from "@/domains/estate/constants/vehiclesTab";
import { IS_DEV } from "@/domains/main/constants/other";

export interface DesignVariant {
  interiorPropId: number;
  isActive: boolean;
  uuid: string;
  price: number;
  type: EstateDesignType;
  name: string | null;
  images: string[];
}

export interface PreviewDesignVariant {
  estateId: number;
  interiorPropId: number;
  name: string;
  type: string;
  price: number;
  address: string;
}

export interface Estate {
  id: number;
  complexId?: number;
  isResident: boolean;
  type: EstateType;
  address: string;
  level: EstateLevel;
}

export type Owner =
  | {
      type: EstateOwner.Character;
      content: {
        playerMpId: number | null;
        characterId: number;
        name: string;
        phoneNumber: string;
      };
    }
  | {
      type: EstateOwner.Family;
      content: {
        name: string;
      };
    };

export interface EstateInfo {
  id: number;
  complexId?: number;
  type: EstateType;
  address: string;
  level: EstateLevel;
  owner: Owner | null;
  vehicles: (string | null)[];

  price: number;
  feeBalance: number | null;
  maxFee: number;
  totalFee: number;
  baseFee: number;

  isResident: boolean;
  isSale: boolean;
  isLocked: boolean;

  tenants: (string | null)[];

  permissions?: {
    garage: boolean;
    fridge: boolean;
    wardrobe: boolean;
    safe: boolean;
    upgrade: boolean;
  };
}

export interface EstateState {
  showSaleConfirm: boolean;
  currentTab: Tab | null;
  tabs: Tab[];

  info: EstateInfo | null;

  tenants: {
    currentTenantId: number | null;
    data: ({
      playerMpId: number | null;
      characterId: number;
      name: string;
      permissions: {
        garage: boolean;
        fridge: boolean;
        wardrobe: boolean;
        safe: boolean;
        upgrade: boolean;
      };
      paidFee: number;
      fee: number;
    } | null)[];
  };

  vehicles: {
    tabs: VehiclesTab[];
    selectedTab: VehiclesTab;
  };

  improvements: {
    tabs: ImprovementsTab[];
    selectedTab: ImprovementsTab;
  };

  mail: {
    currMailId: string | null;
    data: {
      id: string;
      avatarSrc: string | null;
      subject: string;
      from: string;
      to: string;
      date: string | Date;
      isRead: boolean;
    }[];
    messages: {
      messageId: string;
      userId: string;
      avatarSrc: string | null;
      from: string;
      message: string;
      date: string | Date;
      isRead: boolean;
    }[];
  };

  design: {
    previewEnabled: boolean;
    openConfirmBuy: boolean;
    previewedDesignVariant: DesignVariant | null;
    selectedDesignVariant: DesignVariant | null;
    designVariants: DesignVariant[];
    isPresentation: boolean;
    selectedPreviewDesignVariant: PreviewDesignVariant;
  };

  feeSelectedDays: number;

  slots: {
    ownerEstates: Estate[];
    tenantEstates: Estate[];
    tabs: EstateSelectTab[];
    selectedTab: EstateSelectTab;
    selectedEstateId: number;
    settings: {
      ownerCount: number;
      tenantCount: number;
      maxCount: number;
      price: number | null;
    };
  };

  estateEnterInfo: EstateInfo | null;
}

const initialTenants: EstateState["tenants"] = {
  currentTenantId: null,
  data: [],
};

const initalGarage: EstateState["vehicles"] = {
  selectedTab: VehiclesTab.All,
  tabs: [VehiclesTab.All, VehiclesTab.Owner, VehiclesTab.Shared],
};

const initialImprovements: EstateState["improvements"] = {
  tabs: [ImprovementsTab.All, ImprovementsTab.Subscriptions, ImprovementsTab.Improvements],
  selectedTab: ImprovementsTab.All,
};

const initialEstateSelect: EstateState["slots"] = {
  ownerEstates: [],
  tenantEstates: [],
  tabs: [EstateSelectTab.Owner, EstateSelectTab.Tenant],
  selectedTab: EstateSelectTab.Owner,
  selectedEstateId: null,
  settings: {
    ownerCount: 0,
    tenantCount: 0,
    maxCount: 0,
    price: 0,
  },
};

const initalMail: EstateState["mail"] = {
  currMailId: null,
  data: [],
  messages: [],
};

const initialState: EstateState = {
  info: IS_DEV
    ? {
        id: 228,
        type: EstateType.House,
        address: "123 Ocean Drive, Miami",
        isSale: false,
        isResident: true,
        isLocked: false,
        level: EstateLevel.Luxury,
        owner: {
          type: EstateOwner.Character,
          content: {
            name: "Andrew Froggers",
            characterId: 23,
            playerMpId: null,
            phoneNumber: "+7 777 77 76",
          },
        },
        feeBalance: 37000,
        tenants: ["Alice", "Bob", null, null, null, null, null, null],
        vehicles: ["Car1", "Car2", null, null, null],
        price: 1000000,
        totalFee: 200,
        maxFee: 600,
        baseFee: 100,
      }
    : null,

  showSaleConfirm: false,
  currentTab: Tab.Info,
  tabs: [
    Tab.Info,
    Tab.Tenants,
    Tab.Fee,
    Tab.Garage,
    Tab.Walls,
    Tab.Furniture,
    Tab.Improvements,
    // Tab.Mail,
    Tab.History,
  ],

  tenants: { ...initialTenants },
  vehicles: { ...initalGarage },
  improvements: { ...initialImprovements },
  mail: { ...initalMail },
  slots: { ...initialEstateSelect },

  feeSelectedDays: 0,

  design: {
    previewEnabled: false,
    openConfirmBuy: false,
    previewedDesignVariant: null,
    selectedDesignVariant: null,
    designVariants: [],
    isPresentation: false,
    selectedPreviewDesignVariant: null,
  },

  estateEnterInfo: null,
};

const estateSlice = createSlice({
  name: "estateSlice",
  initialState,
  reducers: {
    setEstateInfo: (state, action: PayloadAction<EstateState["info"]>) => {
      state.info = action.payload;
    },
    setEstateBalance: (state, action: PayloadAction<number>) => {
      if (state.info) {
        state.info.feeBalance = action.payload;
      }
    },
    setCurrentTab: (state, action: PayloadAction<Tab>) => {
      state.currentTab = action.payload;
    },
    setShowSaleConfirm: (state, action: PayloadAction<boolean>) => {
      state.showSaleConfirm = action.payload;
    },
    setIsLocked: (state, action: PayloadAction<boolean>) => {
      state.info.isLocked = action.payload;
    },

    // TENANTS
    changeCurrentTenantId: (state, action: PayloadAction<number | undefined>) => {
      state.tenants.currentTenantId = action.payload || null;
    },
    setTenants: (state, action: PayloadAction<EstateState["tenants"]["data"]>) => {
      state.tenants.data = action.payload;
    },
    setTenant: (
      state,
      action: PayloadAction<{ id: number; data: Partial<EstateState["tenants"]["data"][0]> }>,
    ) => {
      const findedTenantIndex = state.tenants.data.findIndex(
        (item) => item.characterId === action.payload.id,
      );
      if (findedTenantIndex > -1) {
        state.tenants.data[findedTenantIndex] = {
          ...state.tenants.data[findedTenantIndex],
          ...action.payload.data,
        };
      }
    },
    removeTenant: (state, action: PayloadAction<number>) => {
      state.tenants.data = [
        ...state.tenants.data.filter((item) =>
          isNull(item) ? true : item.characterId !== action.payload,
        ),
        null,
      ];
    },

    // VEHICLES
    setSelectedVehiclesTab: (state, action: PayloadAction<VehiclesTab>) => {
      state.vehicles.selectedTab = action.payload;
    },
    resetVehicles: (state) => {
      state.vehicles = { ...initalGarage };
    },

    // IMPROVEMENTS
    setSelectedImprovementsTab: (state, action: PayloadAction<ImprovementsTab>) => {
      state.improvements.selectedTab = action.payload;
    },

    // MAIL
    setMail: (state, action: PayloadAction<EstateState["mail"]["data"]>) => {
      state.mail.data = action.payload;
    },
    setMessages: (state, action: PayloadAction<EstateState["mail"]["messages"]>) => {
      state.mail.messages = action.payload;
    },
    addMessage: (
      state,
      action: PayloadAction<EstateState["mail"]["messages"] | EstateState["mail"]["messages"][0]>,
    ) => {
      state.mail.messages = [
        ...(isArray(action.payload) ? action.payload : [action.payload]),
        ...state.mail.messages,
      ];
    },
    changeCurrMailId: (state, action: PayloadAction<string | undefined>) => {
      state.mail.currMailId = action.payload || null;
    },

    // DESIGN
    setDesignVariants: (state, action: PayloadAction<EstateState["design"]["designVariants"]>) => {
      state.design.designVariants = action.payload || [];
    },
    setSelectedDesignVariant: (state, action: PayloadAction<DesignVariant | undefined>) => {
      state.design.selectedDesignVariant = action.payload || null;
    },
    setPreviewedDesignVariant: (state, action: PayloadAction<DesignVariant | undefined>) => {
      state.design.previewedDesignVariant = action.payload || null;
    },
    setDesignOpenConfirmBuy: (state, action: PayloadAction<boolean>) => {
      state.design.openConfirmBuy = action.payload;
    },
    setDesignPreviewEnabled: (state, action: PayloadAction<boolean>) => {
      state.design.previewEnabled = action.payload;
    },
    setDesignIsPresentation: (state, action: PayloadAction<boolean>) => {
      state.design.isPresentation = action.payload;
    },
    setSelectedPreviewDesignVariant: (
      state,
      action: PayloadAction<PreviewDesignVariant | undefined>,
    ) => {
      state.design.selectedPreviewDesignVariant = action.payload || null;
    },

    setFeeSelectedDays: (state, action: PayloadAction<number>) => {
      state.feeSelectedDays = action.payload;
    },
    reset: (state) => {
      state.design.previewedDesignVariant = null;
    },

    // HOUSE SELECT
    setSlotsSelectedTab: (state, action: PayloadAction<EstateSelectTab>) => {
      state.slots.selectedTab = action.payload;
    },
    setSlotsSettings: (state, action: PayloadAction<EstateState["slots"]["settings"]>) => {
      state.slots.settings = action.payload;
    },
    setSlotsTenantEstates: (state, action: PayloadAction<Estate[]>) => {
      state.slots.tenantEstates = action.payload;
    },
    setSlotsOwnerEstates: (state, action: PayloadAction<Estate[]>) => {
      state.slots.ownerEstates = action.payload;
    },
    setSlotsSelectedEstateId: (state, action: PayloadAction<number>) => {
      state.slots.selectedEstateId = action.payload;
    },

    setEstateEnterInfo: (state, action: PayloadAction<EstateInfo | null>) => {
      state.estateEnterInfo = action.payload;
    },

    setInfoIsResident: (state, action: PayloadAction<boolean>) => {
      state.info.isResident = action.payload;
    },

    setTabs: (state, action: PayloadAction<Tab[]>) => {
      state.tabs = action.payload;
    },

    clear: (state) => {
      state.slots.selectedEstateId = null;
      state.currentTab = Tab.Info;
      state.info = null;
      state.tenants.data = [];
      state.tabs = [];
      state.vehicles.selectedTab = VehiclesTab.All;
      state.design.previewEnabled = false;
    },
  },
});

export const estateSelector = (state: RootState) => state.estate.info;
export const currentTabSelector = (state: RootState) => state.estate.currentTab;
export const tabsSelector = (state: RootState) => state.estate.tabs;
export const showSaleConfirmSelector = (state: RootState) => state.estate.showSaleConfirm;
export const tenantsSelector = (state: RootState) => state.estate.tenants;
export const currentTenantSelector = (state: RootState) => {
  return state.estate.tenants.data
    .filter((item) => item)
    .find((item) => item.characterId === state.estate.tenants.currentTenantId);
};

// VEHICLES SELECTORS
export const vehiclesSelectedTabSelector = (state: RootState) => state.estate.vehicles.selectedTab;
export const vehiclesTabsSelector = (state: RootState) => state.estate.vehicles.tabs;

// IMPROVEMENTS SELECTORS
export const improvementsSelectedTabSelector = (state: RootState) =>
  state.estate.improvements.selectedTab;
export const improvementsTabsSelector = (state: RootState) => state.estate.improvements.tabs;

// MAIL SELECTORS
export const mailsSelector = createSelector(
  (state) => state.estate.mail.data,
  (mails: EstateState["mail"]["data"]) => {
    const readMails: EstateState["mail"]["data"] = [];
    const unreadMails: EstateState["mail"]["data"] = [];

    mails.forEach((item) => {
      if (item.isRead) {
        readMails.push(item);
      } else {
        unreadMails.push(item);
      }
    });

    readMails.sort((a, b) => Number(new Date(b.date)) - Number(new Date(a.date)));
    unreadMails.sort((a, b) => Number(new Date(b.date)) - Number(new Date(a.date)));

    return [...unreadMails, ...readMails];
  },
);
export const messagesSelector = createSelector(
  (state) => state.estate.mail.messages,
  (mails: EstateState["mail"]["messages"]) =>
    [...mails].sort((a, b) => Number(new Date(b.date)) - Number(new Date(a.date))),
);
export const currMailSelector = (state: RootState) => {
  const findedMail = state.estate.mail.data
    .filter((item) => item)
    .find((item) => item.id === state.estate.mail.currMailId);

  return findedMail;
};

// DESIGNVARIANTS SELECTORS
export const designVariantsSelector = createSelector(
  (state: RootState) => state.estate.design.designVariants,
  (_, type?: EstateDesignType) => type,
  (designVariants: EstateState["design"]["designVariants"], type?: EstateDesignType) => {
    if (type) return designVariants.filter((item) => item.type === type);
    return designVariants;
  },
);
export const selectedDesignVariantSelector = (state: RootState) => {
  return state.estate.design.selectedDesignVariant;
};
export const previewedDesignVariantSelector = (state: RootState) => {
  return state.estate.design.previewedDesignVariant;
};
export const designOpenConfirmBuySelector = (state: RootState) => {
  return state.estate.design.openConfirmBuy;
};
export const designIsPresentationSelector = (state: RootState) => {
  return state.estate.design.isPresentation;
};
export const designPreviewEnabledSelector = (state: RootState) => {
  return state.estate.design.previewEnabled;
};
export const selectedPreviewDesignVariantSelector = (state: RootState) => {
  return state.estate.design.selectedPreviewDesignVariant;
};

// ESTATE SELECT SELECTORS
export const estateSelectSelectedTabSelector = (state: RootState) => state.estate.slots.selectedTab;
export const estateSelectTabsSelector = (state: RootState) => state.estate.slots.tabs;

export const slotsSelector = (state: RootState) => {
  return state.estate.slots;
};

export const slotsSelectedEstateIdSelector = (state: RootState) => {
  return state.estate.slots.selectedEstateId;
};

// FEE SELECTORS
export const feeSelectedDaysSelector = (state: RootState) => state.estate.feeSelectedDays;

export const estateEnterInfoSelector = (state: RootState) => state.estate.estateEnterInfo;

export const estateReducer = estateSlice.reducer;
export const {
  setCurrentTab,
  setEstateInfo,
  setEstateBalance,
  setIsLocked,
  reset,
  setShowSaleConfirm,
  setTenant,
  changeCurrentTenantId,
  removeTenant,
  setTenants,
  setSelectedVehiclesTab,
  setSelectedImprovementsTab,
  changeCurrMailId,
  setMail,
  setMessages,
  addMessage,
  setDesignVariants,
  setSelectedDesignVariant,
  setPreviewedDesignVariant,
  setDesignOpenConfirmBuy,
  setDesignIsPresentation,
  setDesignPreviewEnabled,
  setSelectedPreviewDesignVariant,
  setFeeSelectedDays,
  setSlotsSelectedTab,
  setSlotsSettings,
  setSlotsTenantEstates,
  setSlotsOwnerEstates,
  setSlotsSelectedEstateId,
  setEstateEnterInfo,
  setInfoIsResident,
  setTabs,
  clear,
} = estateSlice.actions;
